import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const Header = () => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('https://i.ibb.co/MyxNs6ZB/forestry.png');
  const API_BASE = useMemo(() => (import.meta as any)?.env?.VITE_API_URL || (typeof window !== 'undefined' && window.location && window.location.port === '5173' ? 'http://localhost:3002' : ''), []);
  const [headerData, setHeaderData] = useState<any | null>(null);

  // Navigation items from DB (fallback to translations)
  const navItems = [
    { name: headerData?.nav_home_label || t('navigation.home'), url: "/" },
    { name: headerData?.nav_services_label || t('navigation.services'), url: "/teenused" },
    { name: headerData?.nav_about_label || t('navigation.about'), url: "/meist" },
    { name: headerData?.nav_contact_label || t('navigation.contact'), url: "/kontakt" },
  ];

  useEffect(() => {
    (async () => {
      try {
        const [imgRes, hdrRes] = await Promise.all([
          fetch(`${API_BASE}/api/static-images`),
          fetch(`${API_BASE}/api/header`),
        ]);
        try {
          const json = await imgRes.json();
          if (json?.ok && json?.data?.site_logo) setLogoUrl(json.data.site_logo);
        } catch {}
        try {
          const hjson = await hdrRes.json();
          if (hjson?.ok && hjson?.data) setHeaderData(hjson.data);
        } catch {}
      } catch {}
    })();
  }, [API_BASE]);

  const handleMobileMenuClick = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 w-full bg-white/90 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:justify-start md:space-x-10">
          {/* Logo */}
          <div className="flex justify-start lg:w-0 lg:flex-1">
            <Link to="/" className="flex items-center">
              <span className="sr-only">Forestry</span>
              <img 
                src={logoUrl} 
                alt="Forestry logo" 
                className="h-10 w-auto"
              />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="-mr-2 -my-2 md:hidden">
            <button
              type="button"
              onClick={handleMobileMenuClick}
              className="rounded-md p-2 inline-flex items-center justify-center text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
            >
              <span className="sr-only">{t('common.openMenu')}</span>
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden md:flex space-x-10">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.url}
                className="text-base font-medium text-gray-700 hover:text-primary-700 transition-colors"
                onClick={handleLinkClick}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* CTA button */}
          <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0">
            <Link
              to="/kontakt"
              className="ml-8 whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-700 hover:bg-primary-800 transition-colors"
            >
              {headerData?.cta_label || t('navigation.contactUs')}
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden absolute top-full left-0 w-full bg-white shadow-md transform ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.url}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-gray-50 transition-colors"
              onClick={handleLinkClick}
            >
              {item.name}
            </Link>
          ))}
          <Link
            to="/kontakt"
            className="block w-full text-center px-3 py-2 mt-4 rounded-md text-base font-medium text-white bg-primary-700 hover:bg-primary-800"
            onClick={handleLinkClick}
          >
            {headerData?.cta_label || t('navigation.contactUs')}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;